import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { ThemeToggle } from '@/components/app-shell/ThemeToggle';

import { cn } from '@/lib/utils';

import { useAuthStore } from './auth-store';
import { DevLoginScreen } from './DevLoginScreen';
import { isDevImpersonationEnabled } from './dev-impersonation';
import { BankIqLogomark } from './login-icons';
import './login.css';

/**
 * Email format check (SCRUM-355). The email-first flow enters the bank password at
 * the bank IdP after redirect, so on-page validation only needs a well-formed email
 * (SSO mode) plus a non-empty password (password mode — see {@link validateLogin}).
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Mode = 'sso' | 'password';

interface FieldErrors {
  email?: string;
  password?: string;
}

/**
 * Validates the login input for the given mode (SCRUM-658). Both modes require a
 * well-formed email; password mode additionally requires a non-empty password.
 * Inline errors only (the form is `noValidate`); we never show the native bubble.
 */
function validateLogin(email: string, password: string, requirePassword: boolean): FieldErrors {
  const errors: FieldErrors = {};
  const trimmedEmail = email.trim();
  if (!trimmedEmail) {
    errors.email = 'Enter your work email.';
  } else if (!EMAIL_RE.test(trimmedEmail)) {
    errors.email = 'Enter a valid email address.';
  }
  if (requirePassword && password.length === 0) {
    errors.password = 'Enter your password.';
  }
  return errors;
}

/**
 * BankIQ sign-in screen — the first thing an unauthenticated user sees.
 *
 * SCRUM-658 restructures the card into two modes on a familiar SSO-primary layout:
 *
 *  - **SSO mode (default):** a Work email field + a primary "Continue with single
 *    sign-on" button (the existing email-first path: resolve the tenant from the
 *    email domain, then redirect to that tenant's bank IdP — ADR-011 Flow-1), and a
 *    secondary "Sign in with password" button that switches to password mode.
 *  - **Password mode:** Work email + Password fields + a primary "Sign in" button,
 *    with a "Back to single sign-on" control.
 *
 * SCOPE (frontend-only): there is NO new backend password authentication. The
 * password-mode submit routes through the SAME existing sign-in flow (`signIn`,
 * which accepts and currently ignores the password argument — the credential is
 * verified at the bank IdP, not by BankIQ). Real local/CIAM password auth remains
 * the parked SCRUM-452 (Tier-2 Managed Identity). This deliberately avoids
 * reintroducing the unverifiable on-page password field SCRUM-576 removed: no
 * credential is stored, logged, or sent to a BankIQ endpoint that pretends to
 * verify it.
 *
 * The card, inputs, and buttons use the ported `.login-*` styles in login.css (not
 * shadcn primitives) so the focus/hover/active states match Ryan's prototype.
 */
export function LoginPage() {
  // ADR-014: on the dev server with VITE_DEV_IMPERSONATION=true, show the seeded-user
  // picker instead of the IdP flow. Constant-folded away in production builds.
  if (import.meta.env.DEV && isDevImpersonationEnabled()) {
    return <DevLoginScreen />;
  }
  return <LoginPageInner />;
}

function LoginPageInner() {
  const signIn = useAuthStore((s) => s.signIn);
  const signInWithEntra = useAuthStore((s) => s.signInWithEntra);
  const clearError = useAuthStore((s) => s.clearError);
  const isLoading = useAuthStore((s) => s.isLoading);
  const error = useAuthStore((s) => s.error);

  const [mode, setMode] = useState<Mode>('sso');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // The sign-in controls are only disabled while a request is in flight (SCRUM-573).
  const disabled = isLoading;

  // A11y (RT6): move focus to the field the newly shown mode introduces so keyboard /
  // AT users aren't stranded after a mode switch. Runs ONLY when `mode` changes (a
  // user-driven switch), not on first mount — so the SSO view doesn't grab focus on
  // page load. `switchedRef` guards the initial render.
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const switchedRef = useRef(false);
  useEffect(() => {
    if (!switchedRef.current) {
      switchedRef.current = true;
      return;
    }
    if (mode === 'password') {
      passwordInputRef.current?.focus();
    }
  }, [mode]);

  /** Clears a single inline field error as the user corrects it (app-shell 7 oninput).
   *  Rebuilds the object with conditional spread so the cleared key is truly ABSENT
   *  (exactOptionalPropertyTypes forbids assigning `undefined`). */
  function clearFieldError(field: keyof FieldErrors) {
    setFieldErrors((prev) => {
      const keep: FieldErrors = {};
      if (field !== 'email' && prev.email !== undefined) {
        keep.email = prev.email;
      }
      if (field !== 'password' && prev.password !== undefined) {
        keep.password = prev.password;
      }
      return keep;
    });
  }

  /** Switch modes: clear transient field + store errors so state never bleeds across
   *  modes, and drop any entered password when leaving password mode (RT2/RT3). */
  function switchMode(next: Mode) {
    setMode(next);
    setFieldErrors({});
    clearError();
    if (next === 'sso') {
      setPassword('');
    }
  }

  function handleSsoSubmit(event: React.SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const errors = validateLogin(email, password, /* requirePassword */ false);
    setFieldErrors(errors);
    if (errors.email) {
      return;
    }
    // SSO path: resolve the tenant from the email and redirect to its IdP.
    void signInWithEntra(email);
  }

  function handlePasswordSubmit(event: React.SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const errors = validateLogin(email, password, /* requirePassword */ true);
    setFieldErrors(errors);
    if (errors.email || errors.password) {
      return;
    }
    // Routes through the EXISTING sign-in flow; the password is verified at the bank
    // IdP, not by BankIQ (no new backend — SCRUM-452 owns real password auth).
    void signIn(email, password);
  }

  const emailField = (
    <div className="login-field">
      <label className="login-label" htmlFor="loginEmail">
        Work email
      </label>
      <input
        className={cn('login-input', fieldErrors.email && 'is-error')}
        id="loginEmail"
        type="email"
        required
        autoComplete="username"
        placeholder="you@yourbank.com"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (fieldErrors.email) {
            clearFieldError('email');
          }
        }}
        disabled={isLoading}
        aria-invalid={!!fieldErrors.email}
        aria-describedby={fieldErrors.email ? 'loginEmailError' : undefined}
      />
      {fieldErrors.email ? (
        <span id="loginEmailError" role="alert" className="login-error">
          {fieldErrors.email}
        </span>
      ) : null}
    </div>
  );

  // Store-level error = backend/auth failure (distinct from per-field validation).
  const storeError = error ? (
    <p role="alert" className="login-error">
      {error}
    </p>
  ) : null;

  return (
    <div className="bg-bg-2 p-tk-xl relative flex min-h-dvh w-full items-center justify-center overflow-hidden">
      <div className="login-backdrop" aria-hidden="true" />

      <ThemeToggle className="absolute top-6 right-6 z-10" />

      {/* .login-panel carries `position: relative; z-index: 10` so it paints above
          the .login-backdrop pseudo-elements — do not remove (SCRUM-300). */}
      <div className="login-panel">
        {/* Brand */}
        <div className="login-brand">
          <BankIqLogomark className="size-8" />
          <span className="login-logo">
            Bank<span>IQ</span>
          </span>
        </div>

        {/* Hero */}
        <div className="login-hero">
          <h1 className="login-title">Welcome back</h1>
          <p className="login-sub">Sign in to your bank&apos;s intelligence platform.</p>
        </div>

        {mode === 'sso' ? (
          // ── SSO mode (default) ──────────────────────────────────────────────
          <form className="login-form" onSubmit={handleSsoSubmit} noValidate>
            {emailField}

            <p className="login-sub text-text-2 text-sm">
              Enter your work email to continue to your bank&apos;s secure single sign-on.
            </p>

            {storeError}

            <button type="submit" className="login-submit" disabled={disabled}>
              {isLoading ? (
                <>
                  <span className="login-spinner" aria-hidden="true" /> Signing in&hellip;
                </>
              ) : (
                'Continue with single sign-on'
              )}
            </button>

            <div className="login-divider">
              <span>or</span>
            </div>

            <button
              type="button"
              className="login-sso"
              disabled={disabled}
              onClick={() => {
                switchMode('password');
              }}
            >
              Sign in with password
            </button>
          </form>
        ) : (
          // ── Password mode ───────────────────────────────────────────────────
          <form className="login-form" onSubmit={handlePasswordSubmit} noValidate>
            {emailField}

            <div className="login-field">
              <label className="login-label" htmlFor="loginPassword">
                Password
              </label>
              <input
                ref={passwordInputRef}
                className={cn('login-input', fieldErrors.password && 'is-error')}
                id="loginPassword"
                type="password"
                required
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (fieldErrors.password) {
                    clearFieldError('password');
                  }
                }}
                disabled={isLoading}
                aria-invalid={!!fieldErrors.password}
                aria-describedby={fieldErrors.password ? 'loginPasswordError' : undefined}
              />
              {fieldErrors.password ? (
                <span id="loginPasswordError" role="alert" className="login-error">
                  {fieldErrors.password}
                </span>
              ) : null}
            </div>

            {storeError}

            <div className="login-row">
              {/*
                NOTE (SCRUM-296): "Keep me signed in" defaults UNCHECKED — in a
                banking app persistent sessions on shared terminals are a security
                risk. Its real behavior is a SCRUM-102 decision.
              */}
              <label className="login-remember" htmlFor="loginRemember">
                <input id="loginRemember" type="checkbox" disabled={isLoading} />
                Keep me signed in
              </label>
              <button
                type="button"
                className="login-link"
                onClick={() => {
                  // "Forgot password" is out of MVP scope (SSO-only, LOCKED-DECISION #12).
                }}
              >
                Forgot password?
              </button>
            </div>

            <button type="submit" className="login-submit" disabled={disabled}>
              {isLoading ? (
                <>
                  <span className="login-spinner" aria-hidden="true" /> Signing in&hellip;
                </>
              ) : (
                'Sign in'
              )}
            </button>

            <div className="login-divider">
              <span>or</span>
            </div>

            <button
              type="button"
              className="login-sso"
              disabled={disabled}
              onClick={() => {
                switchMode('sso');
              }}
            >
              Back to single sign-on
            </button>
          </form>
        )}

        {/* Footer */}
        <div className="login-foot">
          <span>Powered by Intersect</span>
          <span>&middot;</span>
          <button type="button" className="login-link">
            Privacy
          </button>
          <span>&middot;</span>
          <button type="button" className="login-link">
            Terms
          </button>
        </div>
      </div>
    </div>
  );
}
